import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';

const PAGE_SIZE  = 10;
const TOTAL_DAYS = 60;


// ─── Helpers ──────────────────────────────────────────────────────────────────
const toISO    = (d) => d.toISOString().split('T')[0];
const todayStr = () => toISO(new Date());

const fmtDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

const dayLabel = (iso) => {
  const now = new Date();
  const d1  = new Date(now); d1.setDate(now.getDate() - 1);
  const d2  = new Date(now); d2.setDate(now.getDate() - 2);
  if (iso === toISO(now)) return 'Today';
  if (iso === toISO(d1))  return 'Yesterday';
  if (iso === toISO(d2))  return '2 days ago';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' });
};

const buildDateList = () => {
  const days = [];
  const now  = new Date();
  for (let i = 0; i < TOTAL_DAYS; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    days.push(toISO(d));
  }
  return days;
};

const calcDur = (start, end) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) return null;
  return mins >= 60 ? `${(mins / 60).toFixed(1)}h` : `${mins}m`;
};

// ─── TimeWorkEditor — inline edit with time-range rows ────────────────────────
const TimeWorkEditor = ({ entries, onChange, courses = [], onImport, allNotes = [] }) => {
  const [noteSuggestions, setNoteSuggestions] = useState([]);
  const [activeSuggestionIdx, setActiveSuggestionIdx] = useState(-1); // index of currently highlighted suggestion (-1 = none)
  const [activeRowIdx, setActiveRowIdx] = useState(-1); // which row's suggestions are shown
  const [cursorPos, setCursorPos] = useState(0);
  const inputRefs = useRef({});

  const add = () => onChange([...entries, { startTime: '09:00', endTime: '10:00', note: '' }]);
  const upd = (i, k, v) => { const e = [...entries]; e[i] = { ...e[i], [k]: v }; onChange(e); };
  const del = (i) => onChange(entries.filter((_, idx) => idx !== i));

  // Build vocabulary from all notes + course names + common words
  const buildVocabulary = () => {
    const vocab = new Set();
    // Add course names
    courses.forEach(c => { if (c.courseName) vocab.add(c.courseName); });
    // Add words/phrases from previous notes
    allNotes.forEach(n => {
      if (!n) return;
      // Add the whole note as a phrase (for matching longer sequences)
      if (n.length > 3 && n.length < 80) vocab.add(n);
      // Add individual words (3+ chars)
      n.split(/[\s,;.!?]+/).forEach(w => {
        const trimmed = w.trim();
        if (trimmed.length >= 3) vocab.add(trimmed);
      });
    });
    return Array.from(vocab);
  };

  // Get word being typed at cursor position
  const getCurrentWord = (text, cursor) => {
    // Find word boundaries around cursor
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    // Match the word containing the cursor
    const wordMatch = before.match(/(\S+)$/);
    const word = wordMatch ? wordMatch[1] : '';
    // Find end of current word in after
    const afterMatch = after.match(/^(\S*)/);
    const rest = afterMatch ? afterMatch[1] : '';
    return { word, rest, fullMatch: text.slice(cursor - word.length, cursor + rest.length), start: cursor - word.length, end: cursor + rest.length };
  };

  const handleNoteChange = (i, value) => {
    upd(i, 'note', value);
    setActiveRowIdx(i);

    const inputEl = inputRefs.current[i];
    const cursor = inputEl?.selectionStart ?? value.length;
    setCursorPos(cursor);

    const { word, start, end } = getCurrentWord(value, cursor);
    if (!word || word.length < 1) {
      setNoteSuggestions([]);
      setActiveSuggestionIdx(-1);
      return;
    }

    const lowerWord = word.toLowerCase();
    const vocab = buildVocabulary();
    const matches = vocab
      .filter(v => v.toLowerCase() !== lowerWord && v.toLowerCase().startsWith(lowerWord))
      .sort((a, b) => a.length - b.length) // shorter matches first
      .slice(0, 8);
    setNoteSuggestions(matches);
    setActiveSuggestionIdx(matches.length > 0 ? 0 : -1);
  };

  // Apply a suggestion by inserting it at cursor (not overwriting the whole note)
  const applySuggestion = (rowIdx, suggestion) => {
    const entry = entries[rowIdx];
    if (!entry) return;
    const inputEl = inputRefs.current[rowIdx];
    const cursor = inputEl?.selectionStart ?? entry.note.length;
    const { start, end } = getCurrentWord(entry.note, cursor);
    const newNote = entry.note.slice(0, start) + suggestion + entry.note.slice(end);
    upd(rowIdx, 'note', newNote);
    // Set cursor after the inserted suggestion
    setTimeout(() => {
      if (inputEl) {
        const newCursor = start + suggestion.length;
        inputEl.setSelectionRange(newCursor, newCursor);
        inputEl.focus();
      }
    }, 0);
    setNoteSuggestions([]);
    setActiveSuggestionIdx(-1);
  };

  const handleKeyDown = (i, e) => {
    if (noteSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIdx(prev => (prev + 1) % noteSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIdx(prev => prev <= 0 ? noteSuggestions.length - 1 : prev - 1);
    } else if (e.key === 'Tab' || e.key === 'Enter' && activeSuggestionIdx >= 0) {
      if (activeSuggestionIdx >= 0 && noteSuggestions[activeSuggestionIdx]) {
        e.preventDefault();
        applySuggestion(i, noteSuggestions[activeSuggestionIdx]);
      }
    } else if (e.key === 'Escape') {
      setNoteSuggestions([]);
      setActiveSuggestionIdx(-1);
    }
  };

  return (
    <div className="p-3" style={{ background: '#eef6ff', borderTop: '3px solid #3b82f6' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="small fw-semibold text-primary">
          <i className="bi bi-clock me-1"/>Work Done — Select Time Range &amp; Describe What You Did
        </div>
        {onImport && (
          <button className="btn btn-outline-primary btn-sm" onClick={onImport} title="Import class sessions from Work Attendance">
            <i className="bi bi-download me-1"/>Import from Work Attendance
          </button>
        )}
      </div>

      {entries.length === 0 && (
        <p className="text-muted small fst-italic mb-2">
          Click "Add Time Block" to log your first work entry for this day.
        </p>
      )}

      {entries.length > 0 && (
        <div className="table-responsive mb-2">
          <table className="table table-sm table-bordered bg-white align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 105 }}>From</th>
                <th style={{ width: 105 }}>To</th>
                <th style={{ width: 72 }}>Duration</th>
                <th>Work Done</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const dur = calcDur(e.startTime, e.endTime);
                return (
                  <tr key={i}>
                    <td className="p-1">
                      <input type="time" className="form-control form-control-sm border-0"
                        value={e.startTime}
                        onChange={ev => upd(i, 'startTime', ev.target.value)} />
                    </td>
                    <td className="p-1">
                      <input type="time" className="form-control form-control-sm border-0"
                        value={e.endTime}
                        onChange={ev => upd(i, 'endTime', ev.target.value)} />
                    </td>
                    <td className="text-center p-1">
                      {dur
                        ? <span className="badge bg-primary" style={{ fontSize: 11 }}>{dur}</span>
                        : <span className="text-danger small" title="End must be after start">!</span>}
                    </td>
                    <td className="p-1" style={{ position: 'relative' }}>
                      <input
                        ref={el => { inputRefs.current[i] = el; }}
                        className="form-control form-control-sm border-0"
                        value={e.note}
                        onChange={ev => handleNoteChange(i, ev.target.value)}
                        onKeyDown={ev => handleKeyDown(i, ev)}
                        onFocus={() => setActiveRowIdx(i)}
                        placeholder="What did you work on during this time?"
                        autoComplete="off"
                      />
                      {noteSuggestions.length > 0 && activeRowIdx === i && (
                        <ul className="list-group position-absolute w-100 shadow"
                            style={{ zIndex: 1000, maxHeight: 180, overflowY: 'auto', top: '100%', left: 0 }}>
                          <li className="list-group-item bg-light py-1 px-2 small text-muted"
                              style={{ fontSize: '0.7rem', cursor: 'default' }}>
                            <i className="bi bi-lightbulb me-1"/>Suggestions — ↑↓ to navigate, Tab/Enter to apply, Esc to dismiss
                          </li>
                          {noteSuggestions.map((s, si) => (
                            <li key={si}
                                className={`list-group-item list-group-item-action py-1 px-2 ${si === activeSuggestionIdx ? 'active' : ''}`}
                                style={{ cursor: 'pointer', fontSize: 12 }}
                                onMouseDown={e => e.preventDefault()} /* prevent input blur */
                                onClick={() => applySuggestion(i, s)}>
                              {s}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="text-center p-1">
                      <button className="btn btn-link text-danger btn-sm p-0"
                        onClick={() => del(i)} title="Remove">
                        <i className="bi bi-trash" style={{ fontSize: 13 }}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <button className="btn btn-outline-primary btn-sm" onClick={add}>
        <i className="bi bi-plus-circle me-1"/>Add Time Block
      </button>
    </div>
  );
};

// ─── WorkDisplay — columnar read-only table ───────────────────────────────────
const WorkDisplay = ({ report }) => {
  if (!report) return <span className="text-muted small fst-italic">No entry yet</span>;

  const timeLog = report.timeLog || [];
  const tasks   = (report.tasks   || []).filter(t => t.description?.trim());
  const notes   = report.notes || '';

  if (!timeLog.length && !tasks.length && !notes) {
    return <span className="text-muted small fst-italic">—</span>;
  }

  return (
    <div style={{ fontSize: 13 }}>
      {timeLog.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: 12 }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: 72, whiteSpace: 'nowrap' }}>From</th>
                <th style={{ width: 72, whiteSpace: 'nowrap' }}>To</th>
                <th style={{ width: 72, whiteSpace: 'nowrap' }}>Duration</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {timeLog.map((e, i) => {
                const dur = calcDur(e.startTime, e.endTime);
                return (
                  <tr key={i}>
                    <td className="text-center" style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{e.startTime}</td>
                    <td className="text-center" style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{e.endTime}</td>
                    <td className="text-center">
                      {dur
                        ? <span className="badge bg-primary" style={{ fontSize: 10 }}>{dur}</span>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>{e.note || <span className="text-muted fst-italic">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Fallback for old task-based entries */}
      {!timeLog.length && tasks.map((t, i) => (
        <div key={i} className="d-flex gap-1 mb-1">
          <span className="text-muted flex-shrink-0">•</span>
          <span className="flex-grow-1">{t.description}</span>
          {t.hours > 0 && (
            <span className="badge bg-light text-dark border ms-1 flex-shrink-0" style={{ fontSize: 11 }}>
              {t.hours}h
            </span>
          )}
        </div>
      ))}

      {notes && (
        <div className={`text-muted small ${(timeLog.length || tasks.length) ? 'mt-1 pt-1 border-top' : ''}`}>
          {notes}
        </div>
      )}
    </div>
  );
};

// ─── TodoSection — tasks checklist + tomorrow's plan ──────────────────────────
const TASK_STATUS_OPTS = [
  { value: 'planned',     label: 'Planned',     color: 'secondary' },
  { value: 'in_progress', label: 'In Progress', color: 'warning'   },
  { value: 'done',        label: 'Done',        color: 'success'   },
  { value: 'blocked',     label: 'Blocked',     color: 'danger'    },
];
const PRI_COLOR = { low: 'success', medium: 'primary', high: 'danger' };

const TodoSection = ({ todayReport, yesterdayReport, isReadOnly, todayDate, onSaved, showAlert, forcedTab }) => {
  const [tab,    setTab]    = useState(forcedTab || 'tasks');
  const [tasks,  setTasks]  = useState([]);
  const [plans,  setPlans]  = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (forcedTab) setTab(forcedTab);
  }, [forcedTab]);

  useEffect(() => {
    setTasks((todayReport?.tasks || []).map(t => ({
      description: t.description || '',
      status:      t.status || 'planned',
    })));
    // If today's plannedTasks is empty but yesterday has a plan, show yesterday's plan (auto-carryover)
    const todayPlans = todayReport?.plannedTasks || [];
    const yesterPlans = yesterdayReport?.plannedTasks || [];
    const plansToShow = (todayPlans.length > 0 ? todayPlans : yesterPlans).map(p => ({
      description: p.description || '',
      priority:    p.priority || 'medium',
    }));
    setPlans(plansToShow);
  }, [todayReport, yesterdayReport]);

  const toggleDone = (i) => {
    const t = [...tasks];
    const isDone = t[i].status === 'done' || t[i].status === 'completed';
    t[i] = { ...t[i], status: isDone ? 'planned' : 'done' };
    setTasks(t);
  };
  const updTask = (i, k, v) => { const t = [...tasks]; t[i] = { ...t[i], [k]: v }; setTasks(t); };
  const addTask = () => setTasks([...tasks, { description: '', status: 'planned' }]);
  const delTask = (i) => setTasks(tasks.filter((_, j) => j !== i));

  const updPlan = (i, k, v) => { const p = [...plans]; p[i] = { ...p[i], [k]: v }; setPlans(p); };
  const addPlan = () => setPlans([...plans, { description: '', priority: 'medium' }]);
  const delPlan = (i) => setPlans(plans.filter((_, j) => j !== i));

  const carryOverYesterdayPlan = () => {
    const carried = (yesterdayReport?.plannedTasks || [])
      .filter(p => p.description?.trim())
      .map(p => ({ description: p.description, status: 'planned' }));
    if (carried.length) setTasks(prev => [...prev, ...carried]);
  };

  const yesterdayPlanCount = (yesterdayReport?.plannedTasks || []).filter(p => p.description?.trim()).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.post('/training/daily-reports', {
        date:         todayDate,
        tasks:        tasks.filter(t => t.description.trim()),
        plannedTasks: plans.filter(p => p.description.trim()),
      });
      onSaved();
      showAlert('Tasks & plans saved.');
    } catch (e) {
      showAlert(e?.response?.data?.message || 'Failed to save.', 'danger');
    }
    setSaving(false);
  };

  const doneCount = tasks.filter(t => t.status === 'done' || t.status === 'completed').length;

  return (
    <div className="card mt-3">
      <div className="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex">
          <button
            className={`btn btn-sm rounded-end-0 ${tab === 'tasks' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setTab('tasks')}>
            <i className="bi bi-check2-square me-1"/>Today's Tasks
            {tasks.length > 0 && (
              <span className={`ms-2 badge ${tab === 'tasks' ? 'bg-white text-primary' : 'bg-primary text-white'}`}>
                {doneCount}/{tasks.length}
              </span>
            )}
          </button>
          <button
            className={`btn btn-sm rounded-start-0 ${tab === 'plans' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setTab('plans')}>
            <i className="bi bi-list-task me-1"/>Tomorrow's Plan
            {plans.length > 0 && (
              <span className={`ms-2 badge ${tab === 'plans' ? 'bg-white text-primary' : 'bg-primary text-white'}`}>
                {plans.length}
              </span>
            )}
          </button>
        </div>
        {!isReadOnly && (
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving
              ? <span className="spinner-border spinner-border-sm"/>
              : <><i className="bi bi-cloud-upload me-1"/>Save</>}
          </button>
        )}
      </div>

      <div className="card-body">
        {/* Today's task checklist */}
        {tab === 'tasks' && (
          <div>
            {tasks.length > 0 && (
              <div className="mb-3">
                <div className="progress mb-1" style={{ height: 6 }}>
                  <div className="progress-bar bg-success"
                    style={{ width: tasks.length ? `${(doneCount / tasks.length) * 100}%` : '0%' }}/>
                </div>
                <small className="text-muted">{doneCount} of {tasks.length} completed</small>
              </div>
            )}

            {tasks.length === 0 && (
              <p className="text-muted small fst-italic mb-2">
                {isReadOnly ? 'No tasks listed for today.' : 'No tasks yet — add your first task below.'}
              </p>
            )}

            {tasks.length === 0 && !isReadOnly && yesterdayPlanCount > 0 && (
              <div className="alert alert-info py-2 small d-flex justify-content-between align-items-center mb-2">
                <span>
                  <i className="bi bi-arrow-left-right me-1"/>
                  Yesterday's plan has {yesterdayPlanCount} item(s) — carry them over to today?
                </span>
                <button className="btn btn-sm btn-outline-primary" onClick={carryOverYesterdayPlan}>
                  Carry Over
                </button>
              </div>
            )}

            {tasks.map((t, i) => {
              const isDone = t.status === 'done' || t.status === 'completed';
              const st     = TASK_STATUS_OPTS.find(s => s.value === t.status) || TASK_STATUS_OPTS[0];
              return (
                <div key={i}
                  className={`d-flex align-items-center gap-2 mb-2 p-2 rounded border ${isDone ? 'border-success bg-success-subtle' : 'bg-light'}`}>
                  <button
                    className={`btn p-0 flex-shrink-0 border-0 ${isDone ? 'text-success' : 'text-secondary'}`}
                    style={{ fontSize: 20, lineHeight: 1, background: 'none' }}
                    onClick={() => !isReadOnly && toggleDone(i)}
                    disabled={isReadOnly}>
                    <i className={`bi ${isDone ? 'bi-check-circle-fill' : 'bi-circle'}`}/>
                  </button>

                  {isReadOnly ? (
                    <span className={`flex-grow-1 small ${isDone ? 'text-decoration-line-through text-muted' : ''}`}>
                      {t.description}
                    </span>
                  ) : (
                    <input
                      className="form-control form-control-sm border-0 bg-transparent flex-grow-1"
                      style={isDone ? { textDecoration: 'line-through', color: '#6b7280' } : {}}
                      value={t.description}
                      onChange={e => updTask(i, 'description', e.target.value)}
                      placeholder="Task description..."
                    />
                  )}

                  {!isDone && !isReadOnly && (
                    <select
                      className="form-select form-select-sm border-0 bg-transparent flex-shrink-0"
                      style={{ width: 115 }}
                      value={t.status}
                      onChange={e => updTask(i, 'status', e.target.value)}>
                      {TASK_STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  )}
                  {!isDone && isReadOnly && t.status !== 'planned' && (
                    <span className={`badge bg-${st.color} flex-shrink-0`} style={{ fontSize: 10 }}>
                      {st.label}
                    </span>
                  )}

                  {!isReadOnly && (
                    <button className="btn btn-link text-danger btn-sm p-0 flex-shrink-0"
                      onClick={() => delTask(i)}>
                      <i className="bi bi-x"/>
                    </button>
                  )}
                </div>
              );
            })}

            {!isReadOnly && (
              <button className="btn btn-outline-primary btn-sm mt-1" onClick={addTask}>
                <i className="bi bi-plus me-1"/>Add Task
              </button>
            )}
          </div>
        )}

        {/* Tomorrow's plan */}
        {tab === 'plans' && (
          <div>
            {plans.length === 0 && (
              <p className="text-muted small fst-italic mb-2">
                {isReadOnly ? 'No plans for tomorrow.' : 'No plans yet — add what you plan to do tomorrow.'}
              </p>
            )}

            {plans.map((p, i) => (
              <div key={i} className="d-flex gap-2 mb-2 align-items-center">
                {isReadOnly ? (
                  <>
                    <span className={`badge bg-${PRI_COLOR[p.priority] || 'secondary'} flex-shrink-0`}
                      style={{ fontSize: 10, minWidth: 52 }}>
                      {p.priority}
                    </span>
                    <span className="small flex-grow-1">{p.description}</span>
                  </>
                ) : (
                  <>
                    <select
                      className="form-select form-select-sm flex-shrink-0"
                      style={{ width: 88 }}
                      value={p.priority || 'medium'}
                      onChange={e => updPlan(i, 'priority', e.target.value)}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                    <input
                      className="form-control form-control-sm flex-grow-1"
                      value={p.description}
                      onChange={e => updPlan(i, 'description', e.target.value)}
                      placeholder="What do you plan to do tomorrow?"
                    />
                    <button className="btn btn-link text-danger btn-sm p-0 flex-shrink-0"
                      onClick={() => delPlan(i)}>
                      <i className="bi bi-x"/>
                    </button>
                  </>
                )}
              </div>
            ))}

            {!isReadOnly && (
              <button className="btn btn-outline-primary btn-sm mt-1" onClick={addPlan}>
                <i className="bi bi-plus me-1"/>Add Plan
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function DailyReports() {
  const { user } = useAuth();

  // Support both _id and id field names (auth context differs across environments)
  const myUserId  = user?.id || user?._id || '';
  const role      = user?.roleKey || user?.role || '';
  const allRoles  = [role, ...(user?.additionalRoleKeys || [])];
  const canAdmin  = allRoles.some(r => ['admin', 'manager'].includes(r)) || user?.userType === 'super_admin';

  const [reports,        setReports]        = useState({});
  const [todayReport,    setTodayReport]    = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [editingDate,    setEditingDate]    = useState(null);
  const [editEntries,    setEditEntries]    = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [locking,        setLocking]        = useState(null);
  const [users,          setUsers]          = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [courses,        setCourses]        = useState([]);
  const [page,           setPage]           = useState(1);
  const [refreshKey,     setRefreshKey]     = useState(0);
  const [alert,          setAlert]          = useState({ show: false, msg: '', type: 'success' });
  const [mainTab,        setMainTab]        = useState('daily');

  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });
  const reload    = () => setRefreshKey(k => k + 1);

  const today      = todayStr();
  const allDates   = buildDateList();
  const totalPages = Math.ceil(allDates.length / PAGE_SIZE);
  const pagedDates = allDates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const isOwnView  = !selectedUserId || selectedUserId === myUserId;

  // ── Fetch reports whenever page / user / selectedUser / refreshKey changes ───
  useEffect(() => {
    if (!myUserId) return; // wait until auth context has loaded the user
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const dates     = buildDateList().slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        const startDate = dates[dates.length - 1];
        const endDate   = dates[0];
        // Admin viewing someone else → send their id; everyone else → own id
        const userId = canAdmin && selectedUserId ? selectedUserId : myUserId;
        const r = await api.get('/training/daily-reports', {
          params: { startDate, endDate, limit: PAGE_SIZE + 2, userId },
        });
        if (cancelled) return;
        const records = r.data?.records ?? r.data?.data?.records ?? [];
        const map = {};
        (Array.isArray(records) ? records : []).forEach(rep => {
          map[toISO(new Date(rep.date))] = rep;
        });
        setReports(map);
        setTodayReport(map[today] || null);

        // If on page > 1, separately fetch today's report for the TodoSection
        if (page > 1) {
          const t = await api.get('/training/daily-reports', {
            params: { startDate: today, endDate: today, limit: 1, userId },
          });
          if (!cancelled) {
            const tRecs = t.data?.records ?? t.data?.data?.records ?? [];
            setTodayReport(tRecs[0] || null);
          }
        }
      } catch (e) {
        if (!cancelled) showAlert(e?.response?.data?.message || 'Failed to load reports.', 'danger');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [page, myUserId, selectedUserId, canAdmin, today, refreshKey]); // eslint-disable-line

  // ── Load user list for admin picker ─────────────────────────────────────────
  useEffect(() => {
    if (!canAdmin) return;
    api.get('/users', { params: { limit: 1000 } })
      .then(r => {
        const all = r.data?.records || r.data?.data || [];
        setUsers(all.filter(u => u.roleKey !== 'student'));
      })
      .catch(() => {});
  }, [canAdmin]);

  // ── Load courses for autocomplete ────────────────────────────────────────────
  useEffect(() => {
    api.get('/training/courses', { params: { limit: 500 } })
      .then(r => {
        const list = r.data?.records || r.data?.data || [];
        setCourses(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  // ── Auto-import class sessions from Work Attendance for today ────────────────
  const startEdit = (isoDate) => {
    const rep = reports[isoDate];
    let entries = [];
    if (rep?.timeLog?.length) {
      entries = rep.timeLog.map(e => ({
        startTime: e.startTime || '09:00',
        endTime:   e.endTime   || '10:00',
        note:      e.note      || '',
      }));
    } else if (rep?.tasks?.length) {
      entries = rep.tasks.map(t => ({
        startTime: '09:00',
        endTime:   '10:00',
        note:      t.description || '',
      }));
    }
    setEditingDate(isoDate);
    setEditEntries(entries);
  };

  const cancelEdit = () => setEditingDate(null);

  const importFromWorkAttendance = async () => {
    try {
      const r = await api.get('/training/work-attendance/class-sessions/today');
      const sessions = r.data?.sessions || [];
      if (!sessions.length) {
        alert('No class sessions found for today in Work Attendance.');
        return;
      }
      const entries = sessions.map(s => ({
        startTime: s.startTime ? new Date(s.startTime).toTimeString().slice(0, 5) : '09:00',
        endTime:   s.endTime   ? new Date(s.endTime).toTimeString().slice(0, 5)   : '10:00',
        note:      s.courseName || s.batchName || '',
      }));
      setEditEntries(entries);
    } catch (e) {
      alert('Failed to import class sessions.');
    }
  };

  const handleSave = async (isoDate) => {
    setSaving(true);
    try {
      const timeLog = editEntries
        .filter(e => e.startTime && e.endTime)
        .map(e => ({ startTime: e.startTime, endTime: e.endTime, note: e.note || '' }));
      const res   = await api.post('/training/daily-reports', { date: isoDate, timeLog });
      const saved = res.data?.data || res.data?.record;
      setEditingDate(null);
      if (saved) {
        setReports(prev => ({ ...prev, [isoDate]: saved }));
        if (isoDate === today) setTodayReport(saved);
        showAlert('Work log saved.');
      } else {
        showAlert('Work log saved.');
        reload();
      }
    } catch (e) {
      showAlert(e?.response?.data?.message || 'Failed to save.', 'danger');
    }
    setSaving(false);
  };

  const handleLock = async (rep, lock) => {
    setLocking(rep._id);
    try {
      const r       = await api.patch(`/training/daily-reports/${rep._id}/lock`, { locked: lock });
      const updated = r.data?.data || r.data;
      if (updated?.date) {
        const iso = toISO(new Date(updated.date));
        setReports(prev => ({ ...prev, [iso]: updated }));
        if (iso === today) setTodayReport(updated);
      }
      showAlert(lock ? 'Report locked.' : 'Report unlocked.');
    } catch (e) {
      showAlert(e?.response?.data?.message || 'Failed.', 'danger');
    }
    setLocking(null);
  };

  const handleTodoSaved = () => reload();

  const changePage = (p) => { setPage(p); setEditingDate(null); };
  const selectUser = (id) => { setSelectedUserId(id); setPage(1); setEditingDate(null); };

  const viewingUser = users.find(u => u._id === selectedUserId);

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show}
        onClose={() => setAlert(p => ({ ...p, show: false }))}/>

      <PageHeader title={mainTab === 'tomorrow' ? "Tomorrow's Plan" : "Daily Reports"}
        icon={mainTab === 'tomorrow' ? "bi-list-task" : "bi-calendar-check"}
        subtitle={mainTab === 'tomorrow' ? "Plan your tasks for tomorrow" : "Log your daily work and track team progress"}/>

      {/* Admin: employee dropdown */}
      {canAdmin && (
        <div className="mb-3 d-flex align-items-center gap-2">
          <select className="form-select form-select-sm" style={{ maxWidth: 280 }}
            value={selectedUserId}
            onChange={e => selectUser(e.target.value)}>
            <option value="">My Reports</option>
            {users.map(u => (
              <option key={u._id} value={u._id}>
                {u.firstName} {u.lastName} · {(u.roleKey || '').replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Main tabs: Daily Report / Tomorrow Plan */}
      <div className="mb-3 d-flex gap-1">
        <button
          className={`btn btn-sm ${mainTab === 'daily' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setMainTab('daily')}>
          <i className="bi bi-calendar-check me-1"/>Daily Report
        </button>
        <button
          className={`btn btn-sm ${mainTab === 'tomorrow' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setMainTab('tomorrow')}>
          <i className="bi bi-list-task me-1"/>Tomorrow's Plan
        </button>
      </div>

      {/* Work Log table */}
      {mainTab === 'daily' && (
        <div className="card">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <span className="fw-semibold small">
              <i className="bi bi-clock-history me-2"/>Daily Work Log
              {viewingUser && (
                <span className="text-muted ms-2 fw-normal">
                  — {viewingUser.firstName} {viewingUser.lastName}
                </span>
              )}
            </span>
            <button className="btn btn-outline-secondary btn-sm py-0 px-2"
              onClick={reload} disabled={loading} title="Refresh">
              <i className="bi bi-arrow-clockwise"/>
            </button>
          </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"/>
              <div className="text-muted small mt-2">Loading…</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 120 }}>Date</th>
                    <th style={{ width: 110 }}>Day</th>
                    <th>Work Done (Time — Description)</th>
                    <th style={{ width: 110 }}>Status</th>
                    <th style={{ width: 170 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDates.map(isoDate => {
                    const rep       = reports[isoDate];
                    const isEditing = editingDate === isoDate;
                    const isLocked  = rep?.locked;
                    const isToday   = isoDate === today;
                    // Any user can edit their own non-locked reports — no role restriction
                    const canEdit   = isOwnView && !isLocked;

                    return (
                      <React.Fragment key={isoDate}>
                        <tr className={isEditing ? 'table-primary' : isToday ? 'table-info' : ''}>
                          <td>
                            <div className="fw-semibold small">{fmtDate(isoDate)}</div>
                          </td>
                          <td>
                            <span className={`badge ${isToday ? 'bg-primary' : 'bg-light text-dark border'}`}
                              style={{ fontSize: 11 }}>
                              {dayLabel(isoDate)}
                            </span>
                          </td>
                          <td>
                            {isEditing
                              ? <span className="text-primary small"><i className="bi bi-pencil-square me-1"/>Editing below…</span>
                              : <WorkDisplay report={rep}/>}
                          </td>
                          <td>
                            {isLocked ? (
                              <span className="badge bg-danger"><i className="bi bi-lock-fill me-1"/>Locked</span>
                            ) : rep ? (
                              <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">
                                <i className="bi bi-check-circle me-1"/>Saved
                              </span>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              {isEditing ? (
                                <>
                                  <button className="btn btn-primary btn-sm"
                                    onClick={() => handleSave(isoDate)} disabled={saving}>
                                    {saving
                                      ? <span className="spinner-border spinner-border-sm"/>
                                      : <><i className="bi bi-check-lg me-1"/>Save</>}
                                  </button>
                                  <button className="btn btn-outline-secondary btn-sm" onClick={cancelEdit}>
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  {canEdit && (
                                    <button className="btn btn-outline-primary btn-sm"
                                      onClick={() => startEdit(isoDate)}>
                                      <i className="bi bi-pencil me-1"/>Edit
                                    </button>
                                  )}
                                  {isLocked && isOwnView && !canAdmin && (
                                    <span className="text-danger small">
                                      <i className="bi bi-lock me-1"/>Locked by admin
                                    </span>
                                  )}
                                  {canAdmin && rep && (
                                    <button
                                      className={`btn btn-sm ${isLocked ? 'btn-outline-success' : 'btn-outline-danger'}`}
                                      onClick={() => handleLock(rep, !isLocked)}
                                      disabled={locking === rep._id}>
                                      {locking === rep._id
                                        ? <span className="spinner-border spinner-border-sm"/>
                                        : <>
                                            <i className={`bi ${isLocked ? 'bi-unlock' : 'bi-lock'} me-1`}/>
                                            {isLocked ? 'Unlock' : 'Lock'}
                                          </>}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>

                        {isEditing && (
                          <tr>
                            <td colSpan={5} className="p-0">
                              <TimeWorkEditor entries={editEntries} onChange={setEditEntries} courses={courses} onImport={importFromWorkAttendance} allNotes={Object.values(reports).flatMap(r => (r?.timeLog || []).map(t => t.note).filter(Boolean))}/>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="card-footer py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <small className="text-muted">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, TOTAL_DAYS)} of last {TOTAL_DAYS} days
            </small>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => changePage(Math.max(1, page - 1))}>
                  ‹ Newer
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => (
                <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => changePage(i + 1)}>{i + 1}</button>
                </li>
              ))}
              <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => changePage(Math.min(totalPages, page + 1))}>
                  Older ›
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
      )}

      {/* Tasks & Plans section — always for today's date, per selected user */}
      {(mainTab === 'daily' || mainTab === 'tomorrow') && (
        <TodoSection
          todayReport={todayReport}
          yesterdayReport={reports[allDates[1]]}
          isReadOnly={!isOwnView}
          todayDate={mainTab === 'tomorrow' ? today : today}
          onSaved={handleTodoSaved}
          showAlert={showAlert}
          forcedTab={mainTab === 'tomorrow' ? 'plans' : 'tasks'}
        />
      )}
    </div>
  );
}
